import {Snackbar as MuiSnackbar, SnackbarContent} from '@mui/material';
import {styled} from '@mui/material/styles';
import {CheckCircle, Close, Error, Warning} from '@mui/icons-material';
import React from 'react';
import LegacyButton from '../LegacyButton';
import LegacyIconButton from '../LegacyIconButton';

type LegacySnackbarVariant = 'success' | 'warning' | 'error' | 'info';

const variantIcon: Partial<Record<LegacySnackbarVariant, React.ElementType>> = {
  success: CheckCircle,
  warning: Warning,
  error: Error,
};

const LegacySnackbarContentRoot = styled(SnackbarContent, {
  shouldForwardProp: prop => prop !== 'legacyVariant',
})<{legacyVariant: LegacySnackbarVariant}>(({theme, legacyVariant}) => ({
  margin: theme.spacing(1),
  ...(legacyVariant === 'success' && {backgroundColor: '#32BDC7'}),
  ...(legacyVariant === 'error' && {backgroundColor: theme.palette.error.dark}),
  ...(legacyVariant === 'info' && {backgroundColor: '#e6f1f2', color: '#4a4a4a'}),
  ...(legacyVariant === 'warning' && {backgroundColor: '#FFBC49'}),
  '& .LegacySnackbar-icon': {
    fontSize: 20,
  },
  '& .LegacySnackbar-iconVariant': {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  '& .LegacySnackbar-message': {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'pre-line',
  },
  '& .LegacySnackbar-actionButton': {
    color: '#fff',
    fontSize: 13,
    fontWeight: 500,
    marginRight: -8,
  },
}));

const LegacySnackbarContent = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  const {message, onAction, variant, actionTitle, onClose, noClose, ...other} = props;
  const Icon = variantIcon[variant as LegacySnackbarVariant];
  const action: React.ReactNode[] = [];

  if (onAction) {
    action.push(
      <LegacyButton
        key="action"
        color="primary"
        size="small"
        className="LegacySnackbar-actionButton"
        onClick={() => {
          onAction();
          onClose();
        }}>
        {actionTitle}
      </LegacyButton>,
    );
  }

  if (!noClose && onClose) {
    action.push(
      <LegacyIconButton key="close" aria-label="close" color="inherit" onClick={onClose}>
        <Close className="LegacySnackbar-icon" />
      </LegacyIconButton>,
    );
  }

  return (
    <LegacySnackbarContentRoot
      ref={ref}
      legacyVariant={variant}
      aria-describedby="client-snackbar"
      message={
        <span id="client-snackbar" className="LegacySnackbar-message">
          {Icon ? <Icon className="LegacySnackbar-icon LegacySnackbar-iconVariant" /> : null}
          {message}
        </span>
      }
      action={action}
      {...other}
    />
  );
});

LegacySnackbarContent.displayName = 'LegacySnackbarContent';

const LegacySnackbar = (props: any) => {
  const {className, message, isOpen, variant, actionTitle, onAction, onClose, noClose, ...other} = props;

  return (
    <MuiSnackbar
      anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      open={isOpen}
      autoHideDuration={1000}
      {...other}>
      <LegacySnackbarContent
        variant={variant || 'error'}
        message={message}
        onAction={onAction}
        actionTitle={actionTitle}
        noClose={noClose}
        onClose={onClose}
      />
    </MuiSnackbar>
  );
};

export default LegacySnackbar;
